import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, BellIcon, XIcon, SparklesIcon, FileTextIcon, BookmarkIcon, MessageSquareIcon, BriefcaseIcon, ShoppingBagIcon, UserIcon, LifeBuoyIcon, LockIcon, LogOutIcon } from 'lucide-react';
import { Logo } from './Logo';
import { RegionSelector } from './RegionSelector';
import { useBusiness } from './BusinessContext';
import { useAuth } from './AuthContext';
type NavItem = {
  to: string;
  label: string;
  icon: React.ElementType;
  badge?: boolean;
};
export function MobileHeader() {
  const {
    hub
  } = useBusiness();
  const {
    isAuthenticated,
    openLogin,
    requireAuth,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Close the drawer whenever the route changes.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);
  // Lock body scroll while the drawer is open.
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);
  const navItems: NavItem[] = [{
    to: '/app',
    label: 'Discover',
    icon: SparklesIcon
  }, {
    to: '/requests',
    label: 'Requests',
    icon: FileTextIcon
  }, {
    to: '/saved',
    label: 'Saved',
    icon: BookmarkIcon
  }, {
    to: '/messages',
    label: 'Messages',
    icon: MessageSquareIcon
  }, {
    to: '/business',
    label: 'Business Hub',
    icon: BriefcaseIcon,
    badge: hub?.isNew
  }, ...(hub?.active ? [{
    to: `/shop/${hub.shopSlug || 'shop'}`,
    label: 'My Shop',
    icon: ShoppingBagIcon
  } as NavItem] : []), {
    to: '/profile',
    label: 'Profile',
    icon: UserIcon
  }];
  const handleNav = (to: string) => {
    setOpen(false);
    requireAuth(() => navigate(to));
  };
  return <>
      <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-200 px-4 py-3" data-id="element-2902" data-mp="1ue9hlqw9c7bd">
        <div className="flex items-center justify-between" data-id="element-2903" data-mp="1z9ot4c6slr4m">
          <div className="flex items-center gap-2" data-id="element-2904" data-mp="2454671wz7izz">
            <button onClick={() => setOpen(true)} aria-label="Open menu" aria-expanded={open} className="p-2 -ml-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-2905" data-mp="16ioqa4k76tej">
              <MenuIcon className="w-5 h-5 text-slate-600" data-id="element-2906" data-mp="1gftahyuhgesp" />
            </button>
            <Logo size="sm" data-id="element-2907" data-mp="1lgh2onug71oi" />
          </div>
          <div className="flex items-center gap-2" data-id="element-2908" data-mp="1lh8x3vcb6td7">
            <RegionSelector compact data-id="element-2909" data-mp="2rwjc14nyoifz" />
            <button aria-label="Notifications" className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-2910" data-mp="2rvhwonzwx9sg">
              <BellIcon className="w-5 h-5 text-slate-600" data-id="element-2911" data-mp="l896vlasdeoc" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence data-id="element-2912" data-mp="g9pkjjsa60vg">
        {open && <div className="lg:hidden fixed inset-0 z-[60]" data-id="element-2913" data-mp="gb0njhdwj4li">
            {/* Overlay */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setOpen(false)} className="absolute inset-0 bg-dark-navy/50 backdrop-blur-sm" data-id="element-2914" data-mp="bfv3yp7z220l" />

            {/* Drawer */}
            <motion.aside initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          type: 'spring',
          stiffness: 320,
          damping: 34
        }} className="absolute top-0 left-0 h-full w-[82%] max-w-sm bg-white shadow-2xl flex flex-col" role="dialog" aria-modal="true" data-id="element-2915" data-mp="3ooq23jtw9r4x">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100" data-id="element-2916" data-mp="5mxue8hfnq02">
                <Logo size="sm" data-id="element-2917" data-mp="akzkhdda0pl1" />
                <button onClick={() => setOpen(false)} aria-label="Close menu" className="p-2 -mr-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors" data-id="element-2918" data-mp="ajxy4cqt8go0">
                  <XIcon className="w-5 h-5" data-id="element-2919" data-mp="3j1axmpy6mhaj" />
                </button>
              </div>

              <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-id="element-2920" data-mp="3izhnh0od1exs">
                {navItems.map(item => {
              const locked = !isAuthenticated && item.to !== '/app';
              if (locked) {
                return <button key={item.to} onClick={() => handleNav(item.to)} className="group w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-slate-400 hover:bg-slate-50 transition-all" data-id="element-2921" data-mp="3e0wnn2d0sx9v">
                        <item.icon className="w-5 h-5" data-id="element-2922" data-mp="34fnen83ptit9" />
                        <span className="flex-1 text-left" data-id="element-2923" data-mp="2zfitv5vry1x8">{item.label}</span>
                        <LockIcon className="w-4 h-4 text-slate-300" data-id="element-2924" data-mp="2zhlv6m2dnhh9" />
                      </button>;
              }
              return <NavLink key={item.to} to={item.to} onClick={() => setOpen(false)} className={({
                isActive
              }) => `relative flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all ${item.badge ? 'bg-primary-indigo/5 text-primary-indigo font-medium ring-1 ring-primary-indigo/30' : isActive ? 'bg-primary-indigo/5 text-primary-indigo font-medium' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-2925" data-mp="39cxb37piuuql">
                      {({
                  isActive
                }) => <>
                          <item.icon className={`w-5 h-5 ${isActive || item.badge ? 'text-primary-indigo' : 'text-slate-400'}`} data-id="element-2926" data-mp="7z0ljba05pk8" />
                          <span className="flex-1" data-id="element-2927" data-mp="hsheye6892nd">{item.label}</span>
                          {item.badge && <span className="text-[10px] font-bold uppercase tracking-wide bg-primary-indigo text-white px-2 py-0.5 rounded-full" data-id="element-2928" data-mp="ms2fuu4yjl2q">
                              New
                            </span>}
                        </>}
                    </NavLink>;
            })}

                {/* Help */}
                {isAuthenticated ? <NavLink to="/help" onClick={() => setOpen(false)} className={({
              isActive
            }) => `flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all ${isActive ? 'bg-primary-indigo/5 text-primary-indigo font-medium' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-2929" data-mp="32h406bi847wg">
                    {({
                isActive
              }) => <>
                        <LifeBuoyIcon className={`w-5 h-5 ${isActive ? 'text-primary-indigo' : 'text-slate-400'}`} data-id="element-2930" data-mp="2gajkause7fe5" />
                        <span className="flex-1" data-id="element-2931" data-mp="38yfdhpir6p66">Help</span>
                      </>}
                  </NavLink> : <button onClick={() => {
              setOpen(false);
              requireAuth(() => navigate('/help'));
            }} className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-slate-400 hover:bg-slate-50 transition-all" data-id="element-2932" data-mp="34045lkc0qoql">
                    <LifeBuoyIcon className="w-5 h-5" data-id="element-2933" data-mp="2d7x5l0d10h78" />
                    <span className="flex-1 text-left" data-id="element-2934" data-mp="2dajdj13twypz">Help</span>
                    <LockIcon className="w-4 h-4 text-slate-300" data-id="element-2935" data-mp="2i82q7wkvp3ft" />
                  </button>}
              </nav>

              {/* Auth action */}
              <div className="px-3 py-4 border-t border-slate-100" data-id="element-2936" data-mp="288k7tkkm9ppv">
                {isAuthenticated ? <button onClick={() => {
              setOpen(false);
              logout();
              navigate('/app');
            }} className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-error hover:bg-error/5 transition-colors" data-id="element-2937" data-mp="2vyejvzx43a92">
                    <LogOutIcon className="w-5 h-5" data-id="element-2938" data-mp="2r64viobnai8w" />
                    <span className="flex-1 text-left font-medium" data-id="element-2939" data-mp="3exacj6tbngks">
                      Log out
                    </span>
                  </button> : <button onClick={() => {
              setOpen(false);
              openLogin();
            }} className="w-full py-3.5 bg-primary-indigo text-white font-medium rounded-xl hover:bg-primary-indigo/90 transition-colors" data-id="element-2940" data-mp="189a0hjwhn032">
                    Log in / Sign up
                  </button>}
              </div>
            </motion.aside>
          </div>}
      </AnimatePresence>
    </>;
}